import React, { forwardRef, useState } from "react";

import SentimentVerySatisfiedIcon from "@mui/icons-material/SentimentVerySatisfied";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import { LoadingButton } from "@mui/lab";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Slide,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";

import { postFlightRoutesVote } from "@/api/routes";
import MessageDialog from "@/components/MessageDialog";
import { AirportMapItem } from "@/constants/airport";
import useEvent from "@/hooks/useEvent";
import useSnackbar from "@/hooks/useSnackbar";

import RouteVote from "./RouteVote";

interface RouteRequestCondition {
  programs: { name: string; code: string }[];
  fromLocation: AirportMapItem[];
  toLocation: AirportMapItem[];
}

interface RouteVoteDialogProps {
  open: boolean;
  condition?: RouteRequestCondition;
  onOpen: (value: boolean) => void;
}

const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RouteVoteDialog = (props: RouteVoteDialogProps) => {
  const { open, onOpen } = props;
  const [isLoading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState<{ message: string; status: 0 | 1 }>();

  const [condition, setCondition] = useState<RouteRequestCondition>({
    fromLocation: props.condition?.fromLocation || [],
    toLocation: props.condition?.toLocation || [],
    programs: props.condition?.programs || [],
  });

  const handleChange = useEvent((data) => {
    setCondition({ ...condition, ...data });
  });

  const handleSubmit = useEvent(async () => {
    if (condition.fromLocation.length == 0 && condition.toLocation.length == 0 && condition.programs.length == 0) {
      return;
    }
    
    if (!condition.fromLocation.length) {
      enqueueSnackbar("Please select departure airport");
    } else if (!condition.toLocation.length) {
      enqueueSnackbar("Please select arrival airport");
    } else if (!condition.programs.length) {
      enqueueSnackbar("Please select program");
    } else {
      try {
        setLoading(true);

        const res = await postFlightRoutesVote({
          program: condition.programs.map((ele) => ele.code),
          origin: condition.fromLocation.map((ele) => ele.iata),
          arrival: condition.toLocation.map((ele) => ele.iata),
        });

        handleClose();

        setResult({
          status: 1,
          message: res.message,
        });
      } catch (error: any) {
        if (error?.message) {
          setResult({
            status: 0,
            message: error.message,
          });
        }
      } finally {
        setLoading(false);
      }
    }
  });

  const handleClose = useEvent(() => {
    onOpen(false);
  });

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        className="max-w-[750px]"
        sx={{
          margin: "auto",
          "& .MuiDialog-paper": {
            margin: 0,
            borderRadius: "12px",
            width: "100%",
            maxWidth: "calc(100% - 32px)",
          },
          "& .MuiDialogContent-root": {
            padding: "16px",
          },
        }}
        onClose={handleClose}
      >
        <DialogTitle>
          <DialogContentText sx={{ fontSize: 25 }} color="textPrimary">
            <ThumbUpOffAltIcon
              color="primary"
              sx={{ fontSize: 30, margin: "auto", marginBottom: -0.5, marginRight: 1 }}
            />
            Request Route
          </DialogContentText>
          <DialogContentText color="textPrimary" sx={{ fontSize: 16 }}>
            Tell us your preferred routes.
          </DialogContentText>
          <DialogContentText color="textPrimary" sx={{ fontSize: 16 }}>
            We will typically prepare a full year of data for you in just 30 minutes. And you will
            be notified through email.
          </DialogContentText>
          <DialogContentText color="textPrimary" sx={{ fontSize: 16, display: "flex" }}>
            Monster feature! Give it a shot! <SentimentVerySatisfiedIcon className="ml-1" />
          </DialogContentText>
        </DialogTitle>

        <DialogContent dividers>
          <RouteVote condition={condition} onChange={handleChange} />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onOpen(false)}>Cancel</Button>
          <LoadingButton
            onClick={handleSubmit}
            loading={isLoading}
            loadingPosition="end"
            endIcon={isLoading ? <div className="w-[20px]" /> : <div />}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <MessageDialog
        open={!!result}
        onOpen={() => setResult(undefined)}
        message={result?.message}
        status={result?.status}
      />
    </>
  );
};

export default React.memo(RouteVoteDialog);
